import React, { useState, useCallback, useEffect } from 'react';
import { Button } from '@vibe/core';
import { createWorklog } from '../../api/worklog.api';
import { getWorklogCategories, type WorklogCategory } from '../../api/worklog-category.api';
import type { ScheduleItem } from '../../stores/scheduleStore';
import ItemPicker, { type PickedItem } from '../timetracking/ItemPicker';
import { formatHoursMinutes } from '../../utils/timesheet-capacity';
import { resolveEffectiveUserId } from './log-work-user-resolution';

// ─── Types ────────────────────────────────────────────────────────────────────

const QUICK_DURATIONS = [
  { label: '15m', hours: 0.25 },
  { label: '30m', hours: 0.5 },
  { label: '45m', hours: 0.75 },
  { label: '1h', hours: 1 },
  { label: '2h', hours: 2 },
  { label: '4h', hours: 4 },
  { label: '6h', hours: 6 },
  { label: '8h', hours: 8 },
];

/** Props when opened from the scheduler (task already known) */
interface SchedulerModeProps {
  task: ScheduleItem;
  boardId: number;
  assigneeName: string;
  currentUserId: string;
  /** Admin-only: log work on behalf of another user */
  forUserId?: string;
  forUserName?: string;
  onClose: () => void;
  onWorkLogged?: () => void;
  /** Initial date to pre-fill (optional) */
  initialDate?: string;
  /** Initial end date to pre-fill (optional; defaults to initialDate). Used
   *  when opening from a multi-day drag selection on the scheduler. */
  initialEndDate?: string;
}

/** Props when opened from time tracking (user picks an item) */
interface TimeTrackingModeProps {
  task?: undefined;
  boardId?: undefined;
  assigneeName?: undefined;
  currentUserId: string;
  /** Admin-only: log work on behalf of another user */
  forUserId?: string;
  forUserName?: string;
  onClose: () => void;
  onWorkLogged?: () => void;
  /** Initial date to pre-fill (optional) */
  initialDate?: string;
  /** Initial end date to pre-fill (optional; defaults to initialDate). Used
   *  when opening from a multi-day drag selection on the scheduler. */
  initialEndDate?: string;
}

export type LogWorkDialogProps = SchedulerModeProps | TimeTrackingModeProps;

// ─── Component ────────────────────────────────────────────────────────────────

const LogWorkDialog: React.FC<LogWorkDialogProps> = (props) => {
  const { currentUserId, forUserId, forUserName, onClose, onWorkLogged, initialDate, initialEndDate } = props;
  const task = props.task;
  const boardId = props.boardId;
  const assigneeName = props.assigneeName;

  // Admins opening the dialog from another user's row see the on-behalf banner.
  // This toggle lets them switch the worklog back to their own name without
  // closing and reopening the dialog.
  const [logForSelf, setLogForSelf] = useState(false);
  const effectiveForUserId = logForSelf ? undefined : forUserId;
  const effectiveUserId = resolveEffectiveUserId(forUserId, currentUserId, logForSelf);

  const hasTask = !!task;

  // ── Item picker state (only used when no task is provided) ──────────────
  const [selectedItem, setSelectedItem] = useState<PickedItem | null>(null);

  // ── Time input ─────────────────────────────────────────────────────────────
  const [hoursInput, setHoursInput] = useState('8');
  const [minutesInput, setMinutesInput] = useState('00');
  const [selectedQuick, setSelectedQuick] = useState<number | null>(8);

  // ── Date range ──────────────────────────────────────────────────────────────
  const localToday = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
  const today = initialDate ?? localToday;
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(initialEndDate ?? today);

  // ── Log-many mode (date range) ─────────────────────────────────────────────
  // Auto-enable when the caller provides a multi-day range (e.g. drag-selection
  // on the scheduler); otherwise default to single-day.
  const [logMany, setLogMany] = useState(
    !!(initialEndDate && initialDate && initialEndDate !== initialDate),
  );

  // ── Category ───────────────────────────────────────────────────────────────
  const [categories, setCategories] = useState<WorklogCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');

  useEffect(() => {
    getWorklogCategories(false).then((cats) => {
      setCategories(cats);
      const def = cats.find((c) => c.isDefault) ?? cats[0];
      if (def) setSelectedCategoryId(def.id);
    }).catch(() => {});
  }, []);

  // ── Description ────────────────────────────────────────────────────────────
  const [description, setDescription] = useState('');

  // ── Submission state ────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Compute total hours from input
  const totalHours = (parseFloat(hoursInput) || 0) + (parseFloat(minutesInput) || 0) / 60;

  // Resolve item id and board id from either task or picker
  const resolvedItemId = task ? String(task.mondayItemId) : selectedItem?.id;
  const resolvedBoardId = task ? String(boardId) : selectedItem?.boardId ?? '0';
  const resolvedItemName = task ? task.name : selectedItem?.name ?? '';

  const handleQuickSelect = useCallback((hours: number) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    setHoursInput(String(h));
    setMinutesInput(String(m).padStart(2, '0'));
    setSelectedQuick(hours);
  }, []);

  const handleHoursChange = useCallback((value: string) => {
    setHoursInput(value);
    setSelectedQuick(null);
  }, []);

  const handleMinutesChange = useCallback((value: string) => {
    setMinutesInput(value);
    setSelectedQuick(null);
  }, []);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  /** Return ISO date strings for each working day (Mon-Fri) in [start, end]. */
  const getWorkingDays = useCallback((start: string, end: string): string[] => {
    const days: string[] = [];
    const current = new Date(start + 'T00:00:00');
    const last = new Date(end + 'T00:00:00');
    while (current <= last) {
      const dow = current.getDay(); // 0 = Sun, 6 = Sat
      if (dow !== 0 && dow !== 6) {
        // Use local date components to avoid UTC timezone shift
        const y = current.getFullYear();
        const m = String(current.getMonth() + 1).padStart(2, '0');
        const d = String(current.getDate()).padStart(2, '0');
        days.push(`${y}-${m}-${d}`);
      }
      current.setDate(current.getDate() + 1);
    }
    return days;
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!resolvedItemId) {
      setError('Please select an item');
      return;
    }
    if (totalHours <= 0) {
      setError('Time spent must be greater than 0');
      return;
    }
    if (!startDate) {
      setError('Start date is required');
      return;
    }
    if (logMany && (!endDate || endDate < startDate)) {
      setError('End date must be on or after start date');
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      if (logMany && endDate && endDate !== startDate) {
        // Create one worklog per working day in the range
        const days = getWorkingDays(startDate, endDate);
        if (days.length === 0) {
          setError('No working days in the selected range (weekends are excluded)');
          setSubmitting(false);
          return;
        }
        await Promise.all(
          days.map((day) =>
            createWorklog({
              mondayItemId: resolvedItemId,
              mondayBoardId: resolvedBoardId,
              mondayUserId: effectiveUserId,
              date: day,
              hours: totalHours,
              description: description || null,
              worklogCategoryId: selectedCategoryId || undefined,
            }),
          ),
        );
      } else {
        await createWorklog({
          mondayItemId: resolvedItemId,
          mondayBoardId: resolvedBoardId,
          mondayUserId: effectiveUserId,
          date: startDate,
          hours: totalHours,
          description: description || null,
          worklogCategoryId: selectedCategoryId || undefined,
        });
      }

      onWorkLogged?.();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to log work. Please try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }, [totalHours, startDate, endDate, logMany, resolvedItemId, resolvedBoardId, effectiveUserId, description, selectedCategoryId, getWorkingDays, onClose, onWorkLogged]);

  // Build summary text
  const hoursLabel = totalHours > 0 ? formatHoursMinutes(totalHours) : '0h';
  const workingDays = logMany && endDate && endDate !== startDate ? getWorkingDays(startDate, endDate) : [];
  const summaryItemName = resolvedItemName || 'selected item';
  const summaryText = logMany && workingDays.length > 0
    ? `You are logging ${hoursLabel}/day for ${workingDays.length} working day${workingDays.length > 1 ? 's' : ''} (${startDate} to ${endDate}) to ${summaryItemName}. Total: ${formatHoursMinutes(totalHours * workingDays.length)}`
    : `You are logging ${hoursLabel} of work on ${startDate} to ${summaryItemName}.`;

  const canSubmit = totalHours > 0 && !!startDate && !!resolvedItemId;

  return (
    <div className="rp-create-dialog-overlay" onClick={handleOverlayClick}>
      <div className="rp-log-work-dialog">
        {/* Header */}
        <div className="rp-log-work-dialog__header">
          <h3>Log Work</h3>
          <button className="rp-create-dialog__close" onClick={onClose}>
            &times;
          </button>
        </div>

        {/* Admin note when logging work on behalf of another user */}
        {forUserId && forUserName && (
          <div
            style={{
              background: '#e5f4ff',
              border: '1px solid #0073ea',
              borderRadius: 6,
              padding: '8px 12px',
              margin: '0 20px 0',
              fontSize: 13,
              color: '#323338',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <span>
              {effectiveForUserId ? (
                <>
                  You are logging work on behalf of <strong>{forUserName}</strong>.
                </>
              ) : (
                <>You are logging work for yourself.</>
              )}
            </span>
            <button
              type="button"
              onClick={() => setLogForSelf((v) => !v)}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                color: '#0073ea',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500,
                textDecoration: 'underline',
                whiteSpace: 'nowrap',
              }}
            >
              {effectiveForUserId ? 'Log for myself instead' : `Log for ${forUserName} instead`}
            </button>
          </div>
        )}

        {/* Body */}
        <div className="rp-log-work-dialog__body">
          {/* Left column: form */}
          <div className="rp-log-work-dialog__form">
            {/* Item picker (only when no task is pre-selected) */}
            {!hasTask && (
              <div className="rp-log-work-dialog__section">
                <label className="rp-log-work-dialog__label">Item</label>
                <ItemPicker
                  value={selectedItem}
                  onChange={setSelectedItem}
                  placeholder="Search for an item…"
                />
              </div>
            )}

            {/* Time Spent */}
            <div className="rp-log-work-dialog__section">
              <label className="rp-log-work-dialog__label">Time Spent</label>
              <div className="rp-log-work-dialog__time-row">
                <div className="rp-log-work-dialog__time-input-group">
                  <input
                    type="number"
                    min={0}
                    max={24}
                    value={hoursInput}
                    onChange={(e) => handleHoursChange(e.target.value)}
                    className="rp-log-work-dialog__time-input"
                  />
                  <span className="rp-log-work-dialog__time-unit">h</span>
                  <input
                    type="number"
                    min={0}
                    max={59}
                    step={15}
                    value={minutesInput}
                    onChange={(e) => handleMinutesChange(e.target.value)}
                    className="rp-log-work-dialog__time-input"
                  />
                  <span className="rp-log-work-dialog__time-unit">m</span>
                </div>
                <div className="rp-log-work-dialog__quick-buttons">
                  {QUICK_DURATIONS.map((d) => (
                    <button
                      key={d.label}
                      className={`rp-log-work-dialog__quick-btn${selectedQuick === d.hours ? ' rp-log-work-dialog__quick-btn--active' : ''}`}
                      onClick={() => handleQuickSelect(d.hours)}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Date */}
            <div className="rp-log-work-dialog__section">
              <div className="rp-log-work-dialog__label-row">
                <label className="rp-log-work-dialog__label">Date</label>
                <button
                  className={`rp-log-work-dialog__log-many-btn${logMany ? ' rp-log-work-dialog__log-many-btn--active' : ''}`}
                  onClick={() => setLogMany((v) => !v)}
                >
                  {logMany ? 'Single day' : 'Log many'}
                </button>
              </div>
              {logMany ? (
                <div className="rp-log-work-dialog__date-row">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="rp-log-work-dialog__date-input"
                  />
                  <span className="rp-log-work-dialog__date-separator">—</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="rp-log-work-dialog__date-input"
                  />
                </div>
              ) : (
                <div className="rp-log-work-dialog__date-row">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="rp-log-work-dialog__date-input"
                  />
                </div>
              )}
              {logMany && workingDays.length > 0 && (
                <div className="rp-log-work-dialog__date-hint">
                  {workingDays.length} working day{workingDays.length > 1 ? 's' : ''} (weekends excluded)
                </div>
              )}
            </div>

            {/* Category — hide when only one option (auto-selected) */}
            {categories.length > 1 && (
              <div className="rp-log-work-dialog__section">
                <label className="rp-log-work-dialog__label">Category</label>
                <select
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                  className="rp-log-work-dialog__category-dropdown"
                >
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Work description */}
            <div className="rp-log-work-dialog__section">
              <label className="rp-log-work-dialog__label">Work description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="rp-log-work-dialog__textarea"
                placeholder="Describe what you worked on..."
                rows={4}
              />
            </div>
          </div>

          {/* Right column: selected issue + summary */}
          <div className="rp-log-work-dialog__sidebar">
            <label className="rp-log-work-dialog__label">Selected issue</label>
            {hasTask ? (
              <div className="rp-log-work-dialog__issue-card">
                <div className="rp-log-work-dialog__issue-info">
                  {task.hours > 0 && (
                    <span className="rp-log-work-dialog__issue-hours">{formatHoursMinutes(task.hours)}</span>
                  )}
                  <span className="rp-log-work-dialog__issue-assignee">{assigneeName}</span>
                  {task.status && (
                    <span className="rp-log-work-dialog__issue-status">{task.status}</span>
                  )}
                </div>
                <div className="rp-log-work-dialog__issue-name">{task.name}</div>
              </div>
            ) : selectedItem ? (
              <div className="rp-log-work-dialog__issue-card">
                <div className="rp-log-work-dialog__issue-name">{selectedItem.name}</div>
                {selectedItem.groupName && (
                  <div className="rp-log-work-dialog__issue-info">
                    <span className="rp-log-work-dialog__issue-assignee">{selectedItem.groupName}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="rp-log-work-dialog__issue-card rp-log-work-dialog__issue-card--empty">
                <div className="rp-log-work-dialog__issue-name">No item selected</div>
              </div>
            )}

            <div className="rp-log-work-dialog__summary">
              <div className="rp-log-work-dialog__summary-icon">i</div>
              <span>{summaryText}</span>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="rp-log-work-dialog__error">{error}</div>
        )}

        {/* Footer */}
        <div className="rp-log-work-dialog__footer">
          <div className="rp-log-work-dialog__footer-right">
            <Button
              size={Button.sizes.SMALL}
              onClick={handleSubmit}
              loading={submitting}
              disabled={!canSubmit}
            >
              Submit
            </Button>
            <Button
              kind={Button.kinds.SECONDARY}
              size={Button.sizes.SMALL}
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogWorkDialog;
